import React from 'react';
import { ContactOrganizerRenderer } from './components/ContactOrganizerRenderer';
import {
    ModalErrorBoundary,
    ViewErrorBoundary,
} from './components/ErrorBoundary';
import {
    CollectionContext,
    EnvContext,
    EventContext,
    FormProvider,
    OrganizerContext,
    OverlayProvider,
    UserProvider,
} from './context';
import { ViewController } from './views/ViewController';

export interface ContactOrganizerProps {
    /** The ID of the organizer to be contacted via this widget. */
    organizerId: string;
    /**
     * Visibility of the overlay. The consuming application manages this state.
     */
    isVisible: boolean;
    /**
     * reCAPTCHA site key (PUBLIC), passed from the consuming app's backend/BFF
     * config. Used only when a captcha is deemed necessary.
     */
    captchaKey: string;
    /** Optional callback fired when the widget is closed. */
    onClose?: () => void;
    /**
     * If true, fetches the current user (via the app's BFF) to prefill the
     * email/name fields.
     */
    isAuthenticated?: boolean;
    /**
     * DEFAULT: 'root'. Id of the DOM node this overlay should mount onto.
     * Recommended to be close to the root of the DOM tree.
     */
    domNodeId?: string;
    /**
     * DOM element to mount the modal into (takes precedence over `domNodeId`).
     */
    domNode?: Element;
    /** Optional event id — included on the contact POST when present. */
    eventId?: string;
    /** Optional collection id — included on the contact POST when present. */
    collectionId?: string;
    /**
     * Same-origin BFF path for the contact POST (client → own BFF → Core).
     * Must contain `{id}`. Defaults to `/api/organizers/{id}/contact/`.
     */
    bffContactPath?: string;
    /** Override the BFF path used to fetch the current user for prefill. */
    userPath?: string;
}

export const ContactOrganizer: React.FunctionComponent<
    ContactOrganizerProps
> = (props) => {
    const {
        organizerId,
        eventId,
        collectionId,
        domNode,
        isVisible,
        onClose,
        captchaKey,
        bffContactPath,
        userPath,
        domNodeId = 'root',
    } = props;

    return (
        <ModalErrorBoundary>
            <OverlayProvider isVisible={isVisible} onClose={onClose}>
                <ContactOrganizerRenderer
                    domNodeId={domNodeId}
                    domNode={domNode}
                >
                    <ViewErrorBoundary>
                        <OrganizerContext.Provider value={organizerId}>
                            <EventContext.Provider value={{ eventId }}>
                                <CollectionContext.Provider
                                    value={{ collectionId }}
                                >
                                    <EnvContext.Provider
                                        value={{ captchaKey, bffContactPath }}
                                    >
                                        <UserProvider
                                            isAuthenticated={
                                                props.isAuthenticated
                                            }
                                            userPath={userPath}
                                        >
                                            <FormProvider>
                                                <ViewController />
                                            </FormProvider>
                                        </UserProvider>
                                    </EnvContext.Provider>
                                </CollectionContext.Provider>
                            </EventContext.Provider>
                        </OrganizerContext.Provider>
                    </ViewErrorBoundary>
                </ContactOrganizerRenderer>
            </OverlayProvider>
        </ModalErrorBoundary>
    );
};
